import {onValue} from "firebase/database";
import moment from "moment";
import {useEffect, useRef, useState} from "react";
import {getTypingRef} from "services/database/getTypingRef";
import {updateTypingStatus} from "services/database/updateTypingStatus";
import {sendMessage} from "services/firestore/sendMessage";
import styled from "styled-components";
import {convertHexToRGBA, defaultThemeColors, themeColor} from "ui/theme";
import {formatDateLabel, getAvatarUrl} from "utils";
import {UnreadBadge} from "./UnreadBadge";
import {PrevButton} from "ui/base/Button/EmblaButtons";
import { LoaderComponent } from "ui/base/loading/LoaderComponent";
import {useAuth} from "context/AuthContext";
import {SendIcon} from "../../icons/SendIcon";

export const Chat = ({messages, conversation, sendMessage, currentUserId, onGoBack, isLoading, handleAvatarClick}) => {
    const {userRole} = useAuth();
    const [messageText, setMessageText] = useState("");
    const [isOtherUserTyping, setIsOtherUserTyping] = useState(false);
    const messagesEndRef = useRef(null);
    const typingTimeoutRef = useRef(null);

    const isTeacher = userRole === 2;

    const conversationId = conversation?.firestore_conversation_id;
    const receiverId =
        conversation?.participants &&
        Object.values(conversation?.participants).find(
            (participant) => participant !== currentUserId
        );

    useEffect(() => {
        if (!conversationId || !currentUserId) return;

        if (!receiverId) return;

        const typingRef = getTypingRef(conversationId, receiverId);

        const unsubscribe = onValue(typingRef, (snapshot) => {
            const typingData = snapshot.val();
            setIsOtherUserTyping(typingData?.typing || false);
        });

        return () => unsubscribe();
    }, [conversationId, currentUserId, receiverId]);

    const handleTyping = () => {
        if (conversationId && currentUserId) {
            if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
            }

            updateTypingStatus(conversationId, currentUserId, true);

            typingTimeoutRef.current = setTimeout(() => {
                updateTypingStatus(conversationId, currentUserId, false);
            }, 2000);
        }
    };

    useEffect(() => {
        return () => {
            if (conversationId && currentUserId) {
                updateTypingStatus(conversationId, currentUserId, false);
            }
            if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
            }
        };
    }, [conversationId, currentUserId]);

    const isSentByMe = (msg) => {
        return String(msg.user._id) === String(currentUserId);
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!messageText.trim()) return;

        try {
            if (!receiverId) {
                console.error("Receiver ID not found");
                return;
            }

            setMessageText("");

            updateTypingStatus(conversationId, currentUserId, false);
            sendMessage(messageText)
        } catch (error) {
            console.error("Error sending message:", error);
        }
    };

    const groupMessagesByDate = (messages) => {
        const groups = {};

        messages?.forEach((message) => {
            const date = moment(message.createdAt || new Date()).format("YYYY-MM-DD");
            if (!groups[date]) {
                groups[date] = [];
            }
            groups[date].push(message);
        });

        return groups;
    };
    const messageGroups = groupMessagesByDate(messages);

    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current?.scrollIntoView({behavior: "smooth"});
        }
    }, [messageGroups]);

    return (
        <Container>
            <Header darkBG={isTeacher}>
                {onGoBack ? (<PrevButton onClick={onGoBack} chevronColor={isTeacher ? "white" : null}/>) : null}
                <HeaderLink onClick={() => {
                    handleAvatarClick(receiverId)
                }}>
                    <Avatar>
                        {(isTeacher && conversation?.userData.profile_image) ||
                        (!isTeacher && conversation?.userData.teacher_profile_image) ? (
                            <img
                                src={`${getAvatarUrl(isTeacher ? conversation?.userData?.profile_image : conversation.userData.teacher_profile_image)}`}
                                alt={conversation?.userData?.first_name}
                                width="100%"
                                height="100%"
                                style={{borderRadius: "50%"}}
                            />) : <ChatAvatarLetter>
                            {conversation?.userData?.first_name?.[0] || "?"}
                        </ChatAvatarLetter>}
                    </Avatar>
                    <HeaderContent>
                        <PersonName
                            darkBG={isTeacher}>{`${conversation?.userData?.first_name} ${conversation?.userData?.last_name?.[0]?.toUpperCase()}.`}</PersonName>
                    </HeaderContent>
                </HeaderLink>
            </Header>
            <Body>
                {isLoading ? <LoaderComponent /> : <MessagesContainer className="hide-scrollbar">
                    {Object.keys(messageGroups)
                        .sort()
                        .map((dateKey) => (
                            <div key={dateKey}>
                                <DateLabel>{formatDateLabel(dateKey)}</DateLabel>
                                {messageGroups[dateKey].map((msg) => {
                                    const isMe = isSentByMe(msg);
                                    return (
                                        <MessageWrapper
                                            key={msg._id}
                                            sender={isMe ? "me" : "other"}
                                        >
                                            <MessageBubble sender={isMe ? "me" : "other"}>
                                                <MessageText>{msg.text}</MessageText>
                                            </MessageBubble>
                                            <MessageTime>
                                                {moment(msg?.createdAt).format("h:mm A")}
                                            </MessageTime>
                                        </MessageWrapper>
                                    );
                                })}
                            </div>
                        ))}
                    <div ref={messagesEndRef}/>
                </MessagesContainer>}

                {isOtherUserTyping && (
                    <TypingIndicator>
                        <TypingText>{`${conversation?.userData?.first_name} is typing...`}</TypingText>
                    </TypingIndicator>
                )}
                <InputContainer onSubmit={handleSendMessage}>
                    <MessageInput
                        placeholder="Type a message..."
                        value={messageText}
                        disabled={isLoading}
                        maxLength={250}
                        onChange={(e) => {
                            setMessageText(e.target.value);
                            handleTyping();
                        }}
                        onKeyDown={(e) => e.key === "Enter" && handleSendMessage(e)}
                    />
                    <SendButton onClick={handleSendMessage}>
                        <SendIcon/>
                    </SendButton>
                </InputContainer>
            </Body>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    flex-grow: 1;
    min-height: 0;
    padding-bottom: 1rem;

    @media (max-width: 1024px) {
        max-height: 650px;
    }
`;

const Header = styled.div`
    flex-direction: row;
    display: flex;
    gap: 0.5rem;
    padding-block: 1rem;
    padding-left: ${({darkBG}) => (darkBG ? "2rem" : "0rem")};
    background-color: ${({darkBG}) => (darkBG ? themeColor("darkBlue") : null)};
`;

const HeaderLink = styled.div`
    display: flex;
    align-items: center;
    gap: 1.2rem;
    cursor: pointer;
`;

const HeaderContent = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-grow: 1;
`;

const Avatar = styled.div`
    width: 40px;
    height: 40px;
    border-radius: 50%;
    overflow: hidden;
    background-color: ${themeColor("buttonBgNeutral")};
`;

const PersonName = styled.p`
    font-size: 1rem;
    font-weight: 600;
    color: ${({darkBG}) => (darkBG ? themeColor("white") : themeColor("txt"))};
`;

const Body = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    flex-grow: 1;
    min-height: 0;
    background-color: ${convertHexToRGBA("#A7A6CE", 0.1)};
    border-radius: 10px;
    padding: 1rem;

    @media (max-width: 1024px) {
        max-height: 650px;
    }
`;

const MessagesContainer = styled.div`
    flex-grow: 1;
    padding: 1rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    overflow-y: scroll;
`;

const DateLabel = styled.div`
    text-align: center;
    font-size: 0.875rem;
    color: ${themeColor("txtLight")};
    opacity: 0.5;
    margin-bottom: 1rem;
`;

const MessageWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: ${({sender}) => (sender === "me" ? "flex-end" : "flex-start")};
    gap: 0.25rem;
`;

const MessageBubble = styled.div`
    background-color: ${({sender}) =>
            sender === "me" ? defaultThemeColors.blue : defaultThemeColors.white};
    color: ${({sender}) =>
            sender === "me" ? defaultThemeColors.white : defaultThemeColors.txt};
    border-radius: 8px;
    padding: 0.75rem 1rem;
    max-width: 70%;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    position: relative;
`;

const MessageText = styled.p`
    font-size: 0.875rem;
    margin: 0;
    flex-wrap: wrap;
    word-wrap: break-word;
`;

const MessageTime = styled.span`
    font-size: 0.75rem;
    color: ${themeColor("txt")};
    text-align: right;
    opacity: 0.5;
`;

const InputContainer = styled.form`
    display: flex;
    padding: 1rem;
    gap: 0.5rem;
    align-items: center;
`;

const MessageInput = styled.input`
    flex-grow: 1;
    padding: 0.75rem 1rem;
    border: 1px solid ${themeColor("buttonBgNeutral")};
    border-radius: 8px;
    font-size: 1rem;
    outline: none;
    color: ${themeColor("txt")};

    ::placeholder {
        color: ${themeColor("txt")};
        opacity: 0.5;
    }
`;

const TypingIndicator = styled.div`
    padding: 0 1rem;
    margin-bottom: -0.5rem;
    display: flex;
    justify-content: center;
`;

const TypingText = styled.p`
    font-size: 0.875rem;
    color: ${themeColor("txtLight")};
    font-style: italic;
    margin: 0;
    text-align: center;
`;

 

const ChatAvatarLetter = styled.p`
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    width: 100%;
    height: 100%;
    font-size: 26px;
    color: ${themeColor("blue")};
    font-weight: bold;
    background-color: ${themeColor("lightBlue")};
`;

const SendButton = styled.button`
    outline: none;
    border: none;
    background-color: transparent;
    padding: 0.5rem;
    cursor: pointer;

    &:hover {
        opacity: 0.7;
    }
`